/**
 * In-process accounting for the streaming-path memory-growth hypotheses in
 * inference-api (openai-rest-server). Pure counters — no behavior changes —
 * so a periodic logger can emit one structured snapshot per interval and we
 * can confirm (or refute) each hypothesis against live traffic in SigNoz/Loki.
 *
 * Hypotheses this instruments (H1 and H2 were CONFIRMED against production
 * traffic on 2026-06-11 and fixed in createAbortableAsyncIterable — the
 * counters stay so production can prove the fix and catch regressions):
 *
 *  H1 — `createAbortableAsyncIterable` leaked its `"abort"` listener on
 *       every non-abort exit. Post-fix one listener is attached per
 *       stream and removed when the stream finishes, so
 *       `abortable_<label>_listenersOutstanding` must equal `active`
 *       (regression = grows with request count).
 *
 *  H2 — every `Promise.race([iterator.next(), abortPromise])` iteration
 *       attached a reaction to a never-settling shared `abortPromise`,
 *       pinning ALL chunks of the stream while the AbortController stayed
 *       reachable. Post-fix each race promise dies with its iteration, so
 *       `abortable_<label>_sampledChunksLive` (tracked − GC'd, sampled 1/N)
 *       must return to ~0 after streams end instead of growing.
 *
 *  H3 — a client disconnect on the SSE path (`s.onAbort` in
 *       fastGenerations.ts) only logs; the upstream iterator is never
 *       cancelled, so the server keeps consuming (and buffering) the full
 *       upstream response for nobody. Signature: `sse_chunksAfterClientAbort`
 *       and `sse_msUpstreamRanAfterClientAbort` accumulate. (Acquitted under
 *       abort-free traffic; still open under abort-heavy traffic.)
 *
 * All counters are monotonic except the derived `active`/`live` gauges.
 * Labels are static call-site identifiers (bounded cardinality).
 */

export type AbortableIterableOutcome =
  | "clean"
  | "earlyReturn"
  | "aborted"
  | "errored";

interface AbortableIterableCounters {
  started: number;
  finishedClean: number;
  finishedEarlyReturn: number;
  finishedAborted: number;
  finishedErrored: number;
  peakActive: number;
  chunksYielded: number;
  abortListenersAdded: number;
  abortListenersRemoved: number;
  sampledChunksTracked: number;
  sampledChunksFinalized: number;
}

interface SseStreamCounters {
  started: number;
  finished: number;
  peakActive: number;
  clientAborts: number;
  streamsFinishedAfterClientAbort: number;
  chunksAfterClientAbort: number;
  maxChunksAfterClientAbortInOneStream: number;
  msUpstreamRanAfterClientAbort: number;
}

const DEFAULT_CHUNK_SAMPLING_RATE = 64;

/** Sample 1-in-N chunks into the FinalizationRegistry to bound overhead. */
let chunkSamplingRate = DEFAULT_CHUNK_SAMPLING_RATE;

const abortableCounters = new Map<string, AbortableIterableCounters>();

/**
 * Per-field byte attribution for sampled upstream chunks (H4, below).
 * Keys are dotted paths into the chunk — `choices.0.delta.content`,
 * `usage`, … — so the snapshot names which part of the payload carries the
 * bytes. Paths come from the payload's own shape, which is a bounded,
 * provider-defined schema, and recursion stops at `CHUNK_SHAPE_MAX_DEPTH`.
 */
interface ChunkFieldBytes {
  totalBytes: number;
  maxBytes: number;
  occurrences: number;
}

const chunkShapeBytes = new Map<string, ChunkFieldBytes>();
let chunkShapeSampled = 0;
let chunkShapeTotalBytes = 0;
let chunkShapeMaxBytes = 0;

const DEFAULT_CHUNK_SHAPE_SAMPLING_RATE = 256;

/** Sample 1-in-N chunks into the field-size attribution. */
let chunkShapeSamplingRate = DEFAULT_CHUNK_SHAPE_SAMPLING_RATE;
/**
 * Deep enough to reach `choices.0.delta.content` — the field that carries the
 * payload in a chat-completion chunk, and the one worth attributing bytes to.
 */
const CHUNK_SHAPE_MAX_DEPTH = 4;

const sseCounters: SseStreamCounters = {
  started: 0,
  finished: 0,
  peakActive: 0,
  clientAborts: 0,
  streamsFinishedAfterClientAbort: 0,
  chunksAfterClientAbort: 0,
  maxChunksAfterClientAbortInOneStream: 0,
  msUpstreamRanAfterClientAbort: 0,
};

/**
 * Counts sampled chunk objects the GC has actually reclaimed. The held value
 * is only the label string, so the registry itself never pins a chunk.
 * FinalizationRegistry is missing in some runtimes that import this module
 * transitively (e.g. edge-runtime vitest environments) — degrade to no chunk
 * sampling there instead of throwing at import time.
 */
const chunkFinalizationRegistry =
  typeof FinalizationRegistry === "undefined"
    ? null
    : new FinalizationRegistry<string>((label) => {
        countersFor(label).sampledChunksFinalized += 1;
      });

function countersFor(label: string): AbortableIterableCounters {
  const existing = abortableCounters.get(label);
  if (existing) {
    return existing;
  }
  const fresh: AbortableIterableCounters = {
    started: 0,
    finishedClean: 0,
    finishedEarlyReturn: 0,
    finishedAborted: 0,
    finishedErrored: 0,
    peakActive: 0,
    chunksYielded: 0,
    abortListenersAdded: 0,
    abortListenersRemoved: 0,
    sampledChunksTracked: 0,
    sampledChunksFinalized: 0,
  };
  abortableCounters.set(label, fresh);
  return fresh;
}

function activeFor(c: AbortableIterableCounters): number {
  return (
    c.started -
    c.finishedClean -
    c.finishedEarlyReturn -
    c.finishedAborted -
    c.finishedErrored
  );
}

export function trackAbortableIterableStarted(label: string): void {
  const c = countersFor(label);
  c.started += 1;
  const active = activeFor(c);
  if (active > c.peakActive) {
    c.peakActive = active;
  }
}

export function trackAbortableIterableFinished(
  label: string,
  outcome: AbortableIterableOutcome,
): void {
  const c = countersFor(label);
  switch (outcome) {
    case "clean":
      c.finishedClean += 1;
      break;
    case "earlyReturn":
      c.finishedEarlyReturn += 1;
      break;
    case "aborted":
      c.finishedAborted += 1;
      break;
    case "errored":
      c.finishedErrored += 1;
      break;
  }
}

export function trackAbortListenerAdded(label: string): void {
  countersFor(label).abortListenersAdded += 1;
}

export function trackAbortListenerRemoved(label: string): void {
  countersFor(label).abortListenersRemoved += 1;
}

export function trackAbortableIterableChunk(
  label: string,
  value: unknown,
): void {
  const c = countersFor(label);
  c.chunksYielded += 1;
  if (
    chunkFinalizationRegistry !== null &&
    typeof value === "object" &&
    value !== null &&
    c.chunksYielded % chunkSamplingRate === 0
  ) {
    c.sampledChunksTracked += 1;
    chunkFinalizationRegistry.register(value, label);
  }
}

/**
 * Accumulate the serialized byte size of one sampled upstream chunk, split by
 * field path. Sampled 1-in-`chunkShapeSamplingRate` because it serializes the
 * chunk to measure it.
 *
 * The totals answer "what is actually in these bytes": divide a field's
 * `totalBytes` by `chunkShape_sampled` for its mean contribution per chunk.
 * A field that dominates here is the one to take up with the serving layer.
 */
export function trackChunkShape(value: unknown): void {
  chunkShapeSampled += 1;
  if (chunkShapeSampled % chunkShapeSamplingRate !== 0) {
    return;
  }
  if (typeof value !== "object" || value === null) {
    return;
  }

  let serialized: string;
  try {
    serialized = JSON.stringify(value);
  } catch {
    // Circular or non-serializable payloads are not worth failing a
    // diagnostics path over.
    return;
  }

  chunkShapeTotalBytes += serialized.length;
  if (serialized.length > chunkShapeMaxBytes) {
    chunkShapeMaxBytes = serialized.length;
  }

  recordFieldBytes("", value, 0);
}

function recordFieldBytes(path: string, value: unknown, depth: number): void {
  if (depth >= CHUNK_SHAPE_MAX_DEPTH || typeof value !== "object") {
    return;
  }
  if (value === null) {
    return;
  }

  const entries: [string, unknown][] = Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value);

  for (const [key, child] of entries) {
    // `JSON.stringify(undefined)` returns undefined rather than a string, and
    // an undefined field contributes nothing to the payload either way.
    if (child === undefined) {
      continue;
    }
    const childPath = path === "" ? key : `${path}.${key}`;
    let size: number;
    try {
      size = JSON.stringify(child).length;
    } catch {
      continue;
    }

    const existing = chunkShapeBytes.get(childPath);
    if (existing) {
      existing.totalBytes += size;
      existing.occurrences += 1;
      if (size > existing.maxBytes) {
        existing.maxBytes = size;
      }
    } else {
      chunkShapeBytes.set(childPath, {
        totalBytes: size,
        occurrences: 1,
        maxBytes: size,
      });
    }

    recordFieldBytes(childPath, child, depth + 1);
  }
}

export function trackSseStreamStarted(): void {
  sseCounters.started += 1;
  const active = sseCounters.started - sseCounters.finished;
  if (active > sseCounters.peakActive) {
    sseCounters.peakActive = active;
  }
}

export function trackSseClientAbort(): void {
  sseCounters.clientAborts += 1;
}

export function trackSseStreamFinished({
  clientAborted,
  chunksAfterClientAbort,
  msAfterClientAbort,
}: {
  clientAborted: boolean;
  chunksAfterClientAbort: number;
  msAfterClientAbort: number;
}): void {
  sseCounters.finished += 1;
  if (clientAborted) {
    sseCounters.streamsFinishedAfterClientAbort += 1;
    sseCounters.chunksAfterClientAbort += chunksAfterClientAbort;
    sseCounters.msUpstreamRanAfterClientAbort += msAfterClientAbort;
    if (
      chunksAfterClientAbort > sseCounters.maxChunksAfterClientAbortInOneStream
    ) {
      sseCounters.maxChunksAfterClientAbortInOneStream = chunksAfterClientAbort;
    }
  }
}

export interface StreamDiagnosticsSnapshot {
  abortableIterables: Record<
    string,
    AbortableIterableCounters & {
      active: number;
      listenersOutstanding: number;
      sampledChunksLive: number;
    }
  >;
  sseStreams: SseStreamCounters & { active: number };
}

export function getStreamDiagnosticsSnapshot(): StreamDiagnosticsSnapshot {
  const abortableIterables: StreamDiagnosticsSnapshot["abortableIterables"] =
    {};
  for (const [label, c] of abortableCounters) {
    abortableIterables[label] = {
      ...c,
      active: activeFor(c),
      listenersOutstanding: c.abortListenersAdded - c.abortListenersRemoved,
      sampledChunksLive: c.sampledChunksTracked - c.sampledChunksFinalized,
    };
  }
  return {
    abortableIterables,
    sseStreams: {
      ...sseCounters,
      active: sseCounters.started - sseCounters.finished,
    },
  };
}

/**
 * Flat `snake-ish` keys for one structured log line — easy to filter and
 * chart in SigNoz/Loki (e.g. `abortable_deliverables_sampledChunksLive`).
 */
export function getStreamDiagnosticsLogFields(): Record<string, number> {
  const snapshot = getStreamDiagnosticsSnapshot();
  const fields: Record<string, number> = {};
  for (const [label, c] of Object.entries(snapshot.abortableIterables)) {
    fields[`abortable_${label}_started`] = c.started;
    fields[`abortable_${label}_active`] = c.active;
    fields[`abortable_${label}_peakActive`] = c.peakActive;
    fields[`abortable_${label}_finishedClean`] = c.finishedClean;
    fields[`abortable_${label}_finishedEarlyReturn`] = c.finishedEarlyReturn;
    fields[`abortable_${label}_finishedAborted`] = c.finishedAborted;
    fields[`abortable_${label}_finishedErrored`] = c.finishedErrored;
    fields[`abortable_${label}_chunksYielded`] = c.chunksYielded;
    fields[`abortable_${label}_listenersOutstanding`] = c.listenersOutstanding;
    fields[`abortable_${label}_sampledChunksTracked`] = c.sampledChunksTracked;
    fields[`abortable_${label}_sampledChunksLive`] = c.sampledChunksLive;
  }
  fields.sse_started = snapshot.sseStreams.started;
  fields.sse_active = snapshot.sseStreams.active;
  fields.sse_peakActive = snapshot.sseStreams.peakActive;
  fields.sse_clientAborts = snapshot.sseStreams.clientAborts;
  fields.sse_streamsFinishedAfterClientAbort =
    snapshot.sseStreams.streamsFinishedAfterClientAbort;
  fields.sse_chunksAfterClientAbort =
    snapshot.sseStreams.chunksAfterClientAbort;
  fields.sse_maxChunksAfterClientAbortInOneStream =
    snapshot.sseStreams.maxChunksAfterClientAbortInOneStream;
  fields.sse_msUpstreamRanAfterClientAbort =
    snapshot.sseStreams.msUpstreamRanAfterClientAbort;

  fields.chunkShape_sampled = Math.floor(
    chunkShapeSampled / chunkShapeSamplingRate,
  );
  fields.chunkShape_totalBytes = chunkShapeTotalBytes;
  fields.chunkShape_maxBytes = chunkShapeMaxBytes;
  for (const [path, f] of chunkShapeBytes) {
    fields[`chunkField_${path}_totalBytes`] = f.totalBytes;
    fields[`chunkField_${path}_maxBytes`] = f.maxBytes;
    fields[`chunkField_${path}_occurrences`] = f.occurrences;
  }
  return fields;
}

/** Minimal logger shape so any service logger can be passed in. */
interface MemoryDiagnosticsLogger {
  info(message: string, fields: Record<string, unknown>): void;
}

interface JscHeapStats {
  heapSize: number;
  heapCapacity: number;
  extraMemorySize: number;
  objectCount: number;
  protectedObjectCount: number;
  objectTypeCounts: Record<string, number>;
}

/** Bytes → MiB, rounded to one decimal, for log fields. */
const toMb = (bytes: number) => Math.round((bytes / (1024 * 1024)) * 10) / 10;

// Resolved once at startup. Bun-only: `bun:jsc` exposes the real
// JavaScriptCore heap (process.memoryUsage().heapUsed under-reports it under
// Bun). The specifier is assembled at runtime so non-Bun bundlers (CF
// Workers) never try to resolve it, and any failure leaves it null.
let jscHeapStats: (() => JscHeapStats) | null = null;
let jscHeapStatsLoaded = false;
async function loadJscHeapStats(): Promise<void> {
  if (jscHeapStatsLoaded) return;
  jscHeapStatsLoaded = true;
  try {
    if (typeof (globalThis as { Bun?: unknown }).Bun === "undefined") return;
    const mod = (await import(/* @vite-ignore */ `bun${":"}jsc`)) as {
      heapStats?: () => JscHeapStats;
    };
    jscHeapStats = mod.heapStats ?? null;
  } catch {
    jscHeapStats = null;
  }
}

/**
 * JSC heap fields for the snapshot: total heap/capacity/extra/object counts
 * plus the `topTypes` most numerous object types (as `jsc_obj_<Type>`). The
 * per-type counts are the localizer — the type whose count climbs with RSS
 * over a pod's lifetime is the leak. Empty outside Bun.
 */
function getJscHeapLogFields(topTypes = 15): Record<string, number> {
  if (!jscHeapStats) return {};
  let stats: JscHeapStats;
  try {
    stats = jscHeapStats();
  } catch {
    return {};
  }
  const fields: Record<string, number> = {
    jsc_heapSizeMb: toMb(stats.heapSize),
    jsc_heapCapacityMb: toMb(stats.heapCapacity),
    jsc_extraMemoryMb: toMb(stats.extraMemorySize),
    jsc_objectCount: stats.objectCount,
    jsc_protectedObjectCount: stats.protectedObjectCount,
  };
  const top = Object.entries(stats.objectTypeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topTypes);
  for (const [type, count] of top) {
    fields[`jsc_obj_${type}`] = count;
  }
  return fields;
}

/**
 * Start the periodic memory-diagnostics logger: one structured log line per
 * interval pairing process memory with the streaming-path leak-hypothesis
 * counters above (abortable-iterable listener/chunk retention, SSE streams
 * outliving aborts). Correlate the per-pod slope of `rssMb`/`heapUsedMb`
 * against the `abortable_*`/`sse_*` counters in SigNoz to confirm which
 * hypothesis is responsible.
 *
 * Interval defaults to 60s; override with `MEMORY_DIAGNOSTICS_INTERVAL_MS`
 * (set to 0 to disable). The timer is unref'd so it never keeps the process
 * alive during shutdown.
 */
export function startMemoryDiagnosticsLogging({
  logger,
  extraFields,
}: {
  logger: MemoryDiagnosticsLogger;
  /**
   * Additional flat fields to merge into each snapshot. Lets a service
   * contribute counters this package cannot import — inference-api passes the
   * engine SDK's wire totals, which live downstream of `@kuzco/utils`.
   */
  extraFields?: () => Record<string, number>;
}): void {
  const intervalMs = process.env.MEMORY_DIAGNOSTICS_INTERVAL_MS
    ? Number(process.env.MEMORY_DIAGNOSTICS_INTERVAL_MS)
    : 60_000;

  if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
    return;
  }

  // Resolve the Bun JSC heap-stats hook once; the tick reads it if present.
  void loadJscHeapStats();

  const timer = setInterval(() => {
    const usage = process.memoryUsage();
    const jscFields = getJscHeapLogFields();
    logger.info("memory diagnostics snapshot", {
      uptimeSec: Math.round(process.uptime()),
      rssMb: toMb(usage.rss),
      heapUsedMb: toMb(usage.heapUsed),
      heapTotalMb: toMb(usage.heapTotal),
      externalMb: toMb(usage.external),
      arrayBuffersMb: toMb(usage.arrayBuffers),
      ...offHeapField(usage.rss, jscFields),
      ...getStreamDiagnosticsLogFields(),
      ...jscFields,
      ...extraFields?.(),
    });
  }, intervalMs);
  timer.unref();
}

/**
 * Resident memory the JS engine does not account for: RSS minus the JSC heap
 * capacity and its external/extra memory. This is the number that identifies
 * the 2026-07 inference-api growth — RSS reaches ~2.8 GiB while the JSC heap
 * holds at ~90-190 MB, so ~95% of resident memory is invisible to every
 * heap-shaped counter and to any cap on a JS-side buffer.
 *
 * Empty off Bun, where `getJscHeapLogFields` yields nothing and the
 * subtraction would be meaningless.
 */
function offHeapField(
  rssBytes: number,
  jscFields: Record<string, number>,
): Record<string, number> {
  const heapCapacityMb = jscFields.jsc_heapCapacityMb;
  const extraMemoryMb = jscFields.jsc_extraMemoryMb;
  if (heapCapacityMb === undefined || extraMemoryMb === undefined) {
    return {};
  }
  return {
    offHeapMb:
      Math.round((toMb(rssBytes) - heapCapacityMb - extraMemoryMb) * 10) / 10,
  };
}

/** Test-only: override the 1-in-N chunk GC-sampling rate. */
export function setChunkSamplingRate(rate: number): void {
  chunkSamplingRate = rate >= 1 ? Math.floor(rate) : 1;
}

/** Test-only: override the 1-in-N chunk field-size sampling rate. */
export function setChunkShapeSamplingRate(rate: number): void {
  chunkShapeSamplingRate = rate >= 1 ? Math.floor(rate) : 1;
}

/** Test-only: reset all counters between test cases. */
export function resetStreamDiagnostics(): void {
  abortableCounters.clear();
  sseCounters.started = 0;
  sseCounters.finished = 0;
  sseCounters.peakActive = 0;
  sseCounters.clientAborts = 0;
  sseCounters.streamsFinishedAfterClientAbort = 0;
  sseCounters.chunksAfterClientAbort = 0;
  sseCounters.maxChunksAfterClientAbortInOneStream = 0;
  sseCounters.msUpstreamRanAfterClientAbort = 0;
  chunkSamplingRate = DEFAULT_CHUNK_SAMPLING_RATE;
  chunkShapeBytes.clear();
  chunkShapeSampled = 0;
  chunkShapeTotalBytes = 0;
  chunkShapeMaxBytes = 0;
  chunkShapeSamplingRate = DEFAULT_CHUNK_SHAPE_SAMPLING_RATE;
}
